import { Component, NgZone, ContentChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import Task = gapi.client.tasks.Task;

import { runInAngular } from '../run-in-angular';
import { TasksFilterButtonComponent } from '../tasks-filter-button/tasks-filter-button.component';
import { StoreService } from '../store.service';
import { NameDialogComponent } from '../name-dialog/name-dialog.component';
import { TasksClearParams, TasksDeleteParams, TasksInsertParams, TasksMoveParams, TasksPatchParams } from '../api/tasks-resource-method-params';

const DEBUG = true;

@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss']
})
export class TasksComponent {

  @ContentChild('app-tasks-filter-button') filterButton!: TasksFilterButtonComponent;
  roots$ = new Subject<Task[]>();

  constructor(
    public dialog: MatDialog,
    private store: StoreService,
    private ngZone: NgZone) {
    if (DEBUG) {console.log('new TasksComponent()');}
    this.store.tasks$.pipe(
      map(tasks => tasks?.items ? tasks.items : []),
      map(array => array.filter(task => task.parent===undefined)),
      map((array: Task[]) => array.sort((a: Task, b: Task)=> {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        const first = Number.parseInt(a.position!);
        const second = Number.parseInt(b.position!);
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
        if (DEBUG) console.log('Comparing %d to %d', first, second);
        return (first - second);
      })),
      runInAngular(this.ngZone),
    ).subscribe(value => {
      this.roots$.next(value);
    });
  }

  onSelect(task: Task): void {
    if (DEBUG) {console.log('Task selected:', task);}
    this.store.onTaskSelected(task);
  }

  addTask(): void {
    const dialogRef = this.dialog.open(NameDialogComponent, {
      width: '600px'
    });
    dialogRef.afterClosed().subscribe((data: string) => {
      if (data) {
        const request: TasksInsertParams = {
          tasklist: this.store.taskList!.id!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          resource: {title: data}
        };
        this.store.onTaskAdded(request);
      }
    });
  }

  clearDone(): void {
    const tasklist = this.store.taskList!.id!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const request: TasksClearParams = { tasklist: tasklist };
    this.store.onTaskListCleared(request);
  }

  drop($event: CdkDragDrop<Task[]|null, Task[]>): void {
    // Required paramters: [tasklist, task, parent]
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const tasklist = this.store.taskList!.id!;
    const task = $event.item.data.id;
    const request: TasksMoveParams = {
      tasklist: tasklist,
      task: task,
    };
    // Optional parameters: [previous]
    //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const tasks = $event.container.data!;
    const priorIndex = $event.currentIndex-1;
    const priorId = tasks[priorIndex]?.id;
    if (priorId) {
      request.previous = priorId;
    }
    if (DEBUG) {console.log('Created move request:',request);}

    if ($event.previousContainer===$event.container) {
      moveItemInArray(tasks,
                      $event.previousIndex,
                      $event.currentIndex);
    } else {
      const steps = $event.previousContainer.data;
      transferArrayItem(steps,
                        tasks,
                        $event.previousIndex,
                        $event.currentIndex);
    }
    this.store.onTaskMoved(request);
  }

  onDeleted(task: Task): void {
    if (DEBUG) {console.log('Deleting task:', task);}
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksDeleteParams = {
      tasklist: this.store.taskList!.id!,
      task: task.id!
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    this.store.onTaskDeleted(request);
  }

  onUndeleted(task: Task): void {
    if (DEBUG) {console.log('Undeleting task:', task);}
    task.deleted = false;
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksPatchParams = {
      tasklist: this.store.taskList!.id!,
      task: task.id!,
      resource: task
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    this.store.onTaskUpdated(request);
  }

  onCompleted(task: Task): void {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksPatchParams = {
      tasklist: this.store.taskList!.id!,
      task: task.id!,
      resource: {status: task.status}
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    this.store.onTaskUpdated(request);
  }
}
